<template>
  <teleport to="body">
    <transition name="modal" appear>
      <div class="lf-modal">
        <div class="lf-modal__inner" :class="{ 'lf-modal__inner--leave': isClosing }">
          <div class="lf-modal__headline">
            <slot name="modal-heading"></slot>
          </div>
          <div class="lf-modal__content">
            <slot name="modal-content"></slot>
          </div>
          <div class="lf-modal__close" @click.stop="onClose"></div>
        </div>
      </div>
    </transition>
  </teleport>
</template>

<script lang="ts">
import { defineComponent, ref } from 'vue';

export default defineComponent({
  name: 'Modal',
  emits: ['close-modal'],
  setup(_, context) {
    const isClosing = ref(false);

    const onClose = () => {
      const CLOSE_MODAL_AFTER_MS = 100;
      isClosing.value = true;

      // Set a timeout as the lf__inner needs to scale down first
      setTimeout(() => {
        context.emit('close-modal');
      }, CLOSE_MODAL_AFTER_MS);
    };

    return {
      isClosing,
      onClose
    };
  }
});
</script>

<style lang="scss">
.lf-modal {
  display: flex;
  position: fixed;
  left: 0;
  top: 0;
  right: 0;
  bottom: 0;
  padding-top: 15px;
  justify-content: center;
  z-index: 2005;
  overflow-y: auto;
  overflow-x: hidden;
  letter-spacing: 0;
  font-family: 'Gilroy', Helvetica, Arial, sans-serif;
  -moz-osx-font-smoothing: grayscale;
  -webkit-font-smoothing: antialiased;
  -webkit-text-size-adjust: 100%;
  -webkit-tap-highlight-color: rgba(56, 56, 56, 0);

  &__headline {
    align-items: center;
    display: flex;
    flex-grow: 0;
    height: 50px;
    justify-content: center;
    min-width: 0;
    padding: 0 3rem;
    position: relative;
    color: #ffffff;
    transition: color 125ms linear;
    cursor: pointer;
    background: #222323;
    border-bottom: 1px solid #333;
    letter-spacing: 0.05rem;
  }

  &::before {
    content: '';
    display: block;
    position: absolute;
    top: 0;
    left: 0;
    bottom: 0;
    right: 0;
    background: rgba(#121212, 0.84);
    z-index: 0;
  }

  &__inner {
    width: 1188px;
    min-height: 400px;
    max-width: 100%;
    height: 90%;
    background-color: #5c5c5c;
    box-shadow: 0 0 20px 0 rgba(#000, 0.26);
    color: #2e2e2e;
    position: relative;
    will-change: transform, opacity;
    display: flex;
    flex-wrap: nowrap;
    flex-direction: column;
    z-index: 1;
    animation: modalEnter 0.2s ease forwards;

    &--leave {
      animation: modalLeave 0.1s ease forwards;
    }
    &--header {
      background: red;
    }
  }

  &__close {
    position: fixed;
    top: 0;
    right: 0;
    width: 49px;
    height: 49px;
    background: #252424;
    border-radius: 50%;
    transform: rotate(45deg);
    transition: background-color 125ms linear;
    cursor: pointer;
    z-index: 20;

    .fa {
      display: none;
    }

    &::after,
    &::before {
      content: '';
      display: block;
      position: absolute;
      width: 2px;
      height: 22px;
      top: 50%;
      left: 50%;
      margin-left: -1px;
      margin-top: -12px;
      background: #fff;
      transform-origin: center center;
    }

    &::before {
      transform: rotate(0deg);
    }

    &::after {
      transform: rotate(90deg);
    }

    &:hover {
      background: lighten(#252424, 5%);
    }
  }

  &__tabs {
    display: flex;
    flex-shrink: 0;
    flex-grow: 0;
    width: 100%;
    background: #252424;
  }

  &__tab {
    align-items: center;
    display: flex;
    flex-grow: 0;
    height: 50px;
    justify-content: center;
    min-width: 0;
    padding: 0 3rem;
    position: relative;
    color: #a0a0a0;
    transition: color 125ms linear;
    cursor: pointer;

    &:hover {
      color: #fff;
    }

    &-text {
      display: block;
      font-size: 14px;
      line-height: 1em;
      letter-spacing: 0;
      font-weight: 700;
      max-width: 100%;
      overflow: hidden;
      text-align: center;
      text-decoration: none;
      user-select: none;
    }

    &--active {
      color: #fff;
    }
  }

  &__content {
    position: relative;
    width: 100%;
    height: calc(100% - 50px);
    flex-shrink: 1;
    flex-grow: 1;

    &-tab {
      position: relative;
      height: 100%;
      background-color: #5c5c5c;
      z-index: 10;

      > span {
        display: block;
        height: 100%;
      }
    }
  }

  &-wrapper {
    display: flex;
    align-items: stretch;
    height: 100%;

    &--game-settings,
    &--layout-settings {
      background: #232424;
    }

    &__sidebar {
      position: relative;
      width: 244px;
      flex-shrink: 0;
      flex-grow: 0;
      margin: 0;
      background: #2e2e2e;
      border-left: 8px solid #191919;
      opacity: 1;
      transition: opacity 150ms linear;

      .lf-modal-wrapper--game-settings.lf-modal-wrapper--loading & {
        opacity: 0;
      }

      &-inner {
        position: absolute;
        left: -8px;
        top: 0;
        right: 0;
        bottom: 0;
        padding-left: 8px;
        overflow-x: hidden;
        overflow-y: auto;

        &::-webkit-scrollbar {
          width: 8px;
        }

        /* Track */
        &::-webkit-scrollbar-track {
          background: transparent;
        }

        /* Handle */
        &::-webkit-scrollbar-thumb {
          background: #252424;
        }
      }

      &-item {
        cursor: pointer;
        align-items: center;
        display: flex;
        padding: 16px 20px;
        position: relative;
        text-align: left;
        font-size: 14px;
        line-height: 18px;
        font-weight: 700;
        letter-spacing: 0;
        color: #a0a0a0;
        width: 100%;
        position: relative;
        transition: color 125ms linear, border-color 125ms linear;
        animation: fadeIn 200ms $transition-easing-default 0ms backwards;

        &:nth-child(2) .lf-modal-wrapper__content-item-block {
          animation-delay: 30ms;
        }
        &:nth-child(3) .lf-modal-wrapper__content-item-block {
          animation-delay: 60ms;
        }
        &:nth-child(4) .lf-modal-wrapper__content-item-block {
          animation-delay: 90ms;
        }
        &:nth-child(5) .lf-modal-wrapper__content-item-block {
          animation-delay: 120ms;
        }
        &:nth-child(6) .lf-modal-wrapper__content-item-block {
          animation-delay: 150ms;
        }
        &:nth-child(7) .lf-modal-wrapper__content-item-block {
          animation-delay: 180ms;
        }
        &:nth-child(8) .lf-modal-wrapper__content-item-block {
          animation-delay: 210ms;
        }
        &:nth-child(9) .lf-modal-wrapper__content-item-block {
          animation-delay: 240ms;
        }
        &:nth-child(10) .lf-modal-wrapper__content-item-block {
          animation-delay: 270ms;
        }
        &:nth-child(11) .lf-modal-wrapper__content-item-block {
          animation-delay: 300ms;
        }
        &:nth-child(12) .lf-modal-wrapper__content-item-block {
          animation-delay: 330ms;
        }
        &:nth-child(13) .lf-modal-wrapper__content-item-block {
          animation-delay: 360ms;
        }
        &:nth-child(14) .lf-modal-wrapper__content-item-block {
          animation-delay: 390ms;
        }
        &:nth-child(15) .lf-modal-wrapper__content-item-block {
          animation-delay: 420ms;
        }
        &:nth-child(16) .lf-modal-wrapper__content-item-block {
          animation-delay: 450ms;
        }
        &:nth-child(17) .lf-modal-wrapper__content-item-block {
          animation-delay: 480ms;
        }
        &:nth-child(18) .lf-modal-wrapper__content-item-block {
          animation-delay: 510ms;
        }
        &:nth-child(19) .lf-modal-wrapper__content-item-block {
          animation-delay: 540ms;
        }
        &:nth-child(20) .lf-modal-wrapper__content-item-block {
          animation-delay: 570ms;
        }
        &:nth-child(21) .lf-modal-wrapper__content-item-block {
          animation-delay: 570ms;
        }
        &:nth-child(22) .lf-modal-wrapper__content-item-block {
          animation-delay: 570ms;
        }
        &:nth-child(23) .lf-modal-wrapper__content-item-block {
          animation-delay: 570ms;
        }
        &:nth-child(24) .lf-modal-wrapper__content-item-block {
          animation-delay: 570ms;
        }
        &:nth-child(25) .lf-modal-wrapper__content-item-block {
          animation-delay: 570ms;
        }
        &:nth-child(26) .lf-modal-wrapper__content-item-block {
          animation-delay: 570ms;
        }
        &:nth-child(27) .lf-modal-wrapper__content-item-block {
          animation-delay: 570ms;
        }
        &:nth-child(28) .lf-modal-wrapper__content-item-block {
          animation-delay: 570ms;
        }
        &:nth-child(29) .lf-modal-wrapper__content-item-block {
          animation-delay: 570ms;
        }
        &:nth-child(30) .lf-modal-wrapper__content-item-block {
          animation-delay: 570ms;
        }

        &::after {
          content: '';
          display: block;
          height: 100%;
          background-color: #144eff;
          width: 0;
          position: absolute;
          left: -8px;
          top: 0;
          pointer-events: none;
          transition: width 125ms linear;
        }

        &-text {
          display: inline-block;
          flex-grow: 1;
          max-width: 100%;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        &:hover {
          color: #ffffff;
        }

        &--active {
          color: #ffffff;
          pointer-events: none;

          &::after {
            width: 8px;
          }
        }
      }
    }

    &__content {
      display: block;
      width: 100%;
      flex-shrink: 1;
      flex-grow: 1;
      position: relative;

      &-iframe {
        display: block;
        width: 100%;
        height: 100%;
        position: absolute;
        left: 0;
        top: 0;
        border: none;
        background-color: #222323;
        opacity: 1;
        transition: opacity 150ms linear;

        .lf-modal-wrapper--loading & {
          opacity: 0;
        }
      }

      &-empty {
        display: flex;
        align-items: center;
        justify-content: center;
        position: absolute;
        width: 108px;
        height: 108px;
        top: 50%;
        left: 50%;
        margin-left: -54px;
        margin-top: -54px;
        padding: 10px 30px 15px 30px;
        text-align: center;
        font-size: 11px;
        line-height: 12px;
        color: #b7b7b7;
        font-weight: 800;
        text-transform: uppercase;
        z-index: 1;
        animation: fadeIn 375ms linear both;

        &-icon {
          display: block;
          position: absolute;
          top: 0;
          left: 0;
          font-size: 108px;
          line-height: 1em;
          color: #696969;
          z-index: -1;
        }
      }

      &-inner {
        display: block;
        overflow-y: auto;
        overflow-x: hidden;
        padding: 15px;
        position: absolute;
        left: 0;
        top: 0;
        width: 100%;
        height: 100%;

        &::after {
          content: '';
          display: table;
          clear: both;
        }

        &::-webkit-scrollbar {
          width: 8px;
        }

        /* Track */
        &::-webkit-scrollbar-track {
          background: transparent;
        }

        /* Handle */
        &::-webkit-scrollbar-thumb {
          background: #2e2e2e;
        }
      }

      &-item {
        float: left;
        width: 226px;
        padding: 15px;

        .lf-modal__content-tab--grid & {
          width: 150px;
        }

        &:nth-child(2) .lf-modal-wrapper__content-item-block {
          animation-delay: 30ms;
        }
        &:nth-child(3) .lf-modal-wrapper__content-item-block {
          animation-delay: 60ms;
        }
        &:nth-child(4) .lf-modal-wrapper__content-item-block {
          animation-delay: 90ms;
        }
        &:nth-child(5) .lf-modal-wrapper__content-item-block {
          animation-delay: 120ms;
        }
        &:nth-child(6) .lf-modal-wrapper__content-item-block {
          animation-delay: 150ms;
        }
        &:nth-child(7) .lf-modal-wrapper__content-item-block {
          animation-delay: 180ms;
        }
        &:nth-child(8) .lf-modal-wrapper__content-item-block {
          animation-delay: 210ms;
        }
        &:nth-child(9) .lf-modal-wrapper__content-item-block {
          animation-delay: 240ms;
        }
        &:nth-child(10) .lf-modal-wrapper__content-item-block {
          animation-delay: 270ms;
        }
        &:nth-child(11) .lf-modal-wrapper__content-item-block {
          animation-delay: 300ms;
        }
        &:nth-child(12) .lf-modal-wrapper__content-item-block {
          animation-delay: 330ms;
        }
        &:nth-child(13) .lf-modal-wrapper__content-item-block {
          animation-delay: 360ms;
        }
        &:nth-child(14) .lf-modal-wrapper__content-item-block {
          animation-delay: 390ms;
        }
        &:nth-child(15) .lf-modal-wrapper__content-item-block {
          animation-delay: 420ms;
        }
        &:nth-child(16) .lf-modal-wrapper__content-item-block {
          animation-delay: 450ms;
        }
        &:nth-child(17) .lf-modal-wrapper__content-item-block {
          animation-delay: 480ms;
        }
        &:nth-child(18) .lf-modal-wrapper__content-item-block {
          animation-delay: 510ms;
        }
        &:nth-child(19) .lf-modal-wrapper__content-item-block {
          animation-delay: 540ms;
        }
        &:nth-child(20) .lf-modal-wrapper__content-item-block {
          animation-delay: 570ms;
        }
        &:nth-child(21) .lf-modal-wrapper__content-item-block {
          animation-delay: 570ms;
        }
        &:nth-child(22) .lf-modal-wrapper__content-item-block {
          animation-delay: 570ms;
        }
        &:nth-child(23) .lf-modal-wrapper__content-item-block {
          animation-delay: 570ms;
        }
        &:nth-child(24) .lf-modal-wrapper__content-item-block {
          animation-delay: 570ms;
        }
        &:nth-child(25) .lf-modal-wrapper__content-item-block {
          animation-delay: 570ms;
        }
        &:nth-child(26) .lf-modal-wrapper__content-item-block {
          animation-delay: 570ms;
        }
        &:nth-child(27) .lf-modal-wrapper__content-item-block {
          animation-delay: 570ms;
        }
        &:nth-child(28) .lf-modal-wrapper__content-item-block {
          animation-delay: 570ms;
        }
        &:nth-child(29) .lf-modal-wrapper__content-item-block {
          animation-delay: 570ms;
        }
        &:nth-child(20) .lf-modal-wrapper__content-item-block {
          animation-delay: 570ms;
        }

        &-block {
          padding: 20px 0 15px 0;
          background: #696969;
          backface-visibility: hidden;
          transform-style: preserve-3d;
          will-change: transform, opacity;
          animation: itemEnter 450ms $transition-easing-default 0ms backwards;
          transition: color 125ms linear;
          color: #cfcfcf;
          text-align: center;
          cursor: pointer;

          &:hover {
            .btn {
              background: lighten(#2e2e2e, 10%) !important;
            }
          }

          &-img {
            width: 100%;
            height: auto;
            position: relative;
            margin-top: -20px;

            &-inner {
              display: block;

              .lf-modal__content-tab--grid & {
                margin-bottom: 5px;
              }

              img {
                display: inline-block;
                margin: 10px 10px 0 10px;
                height: auto;
                width: calc(100% - 20px);
                object-fit: fill;
              }
            }

            &-text {
              display: block;
              height: 35px;
              font-weight: 600;
              font-size: 12px;
              line-height: 15px;
              margin-top: 15px;
              letter-spacing: 0;
              padding: 0 10px;
              text-align: center;

              overflow: hidden;
              text-overflow: ellipsis;
              -webkit-line-clamp: 2;
              -webkit-box-orient: vertical;

              .lf-modal__content-tab--grid & {
                display: none;
              }
            }
          }

          &-icon {
            width: 100%;
            height: auto;
            position: relative;

            &-inner {
              display: block;

              .fa {
                display: inline-block;
                font-size: 35px;
                color: #454545;
                transform: scale(1) translateZ(0);
                backface-visibility: hidden;
                transform-style: preserve-3d;
                will-change: transform;
                transition: transform 500ms $transition-easing-default;
              }

              i {
                display: block;
                height: 34px;
                line-height: 30px;
                color: #454545;
              }

              .icon--game {
                display: block;
                height: 50px;
                line-height: 50px;
                font-size: 50px;
              }

              .icon-addon-text,
              .icon-section,
              .icon-row,
              .icon-column,
              .icon-game {
                font-size: 31px;
              }

              .icon-addon-rocket {
                position: relative;
                top: 2px;
                font-size: 32px;
              }

              .icon-addon-button,
              .icon-addon-divider {
                position: relative;
                top: 1px;
                font-size: 23px;
              }

              .icon-addon-image {
                font-size: 19px;
              }

              .icon-addon-headline {
                font-size: 31px;
              }
            }

            &-text {
              display: block;
              height: 30px;
              font-weight: 600;
              font-size: 12px;
              line-height: 15px;
              margin-top: 10px;
              padding: 0 15px;
              text-align: center;

              overflow: hidden;
              text-overflow: ellipsis;
              -webkit-line-clamp: 2;
              -webkit-box-orient: vertical;
            }
          }

          &-text {
            display: block;
            max-width: 100%;
            overflow: hidden;
            padding: 0;
            transition: background-color 300ms linear;

            .btn {
              width: 26px;
              height: 26px;
              margin: 10px auto 0 auto;
              padding: 0;
              border-radius: 50%;
              border: 0;
              background: #5c5c5c;
              color: #b2b2b2;
              font-size: 12px;
              line-height: 28px;
              transition: background-color 125ms linear;

              .icon-arrow-toggle {
                display: inline-block;
                padding-left: 1px;
              }

              .icon-plus {
                font-size: 11px;
              }

              &:hover,
              &:active {
                background: lighten(#2e2e2e, 10%) !important;
              }

              &--info {
                &:hover,
                &:active {
                  background: lighten(#2e2e2e, 10%) !important;
                }
              }
            }
          }
        }
      }

      &-faq {
        position: absolute;
        left: 0;
        top: 0;
        right: 0;
        bottom: 0;
        background-color: #fff;

        &::before {
          content: '';
          display: block;
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          height: 30px;
          z-index: 1;
          background: #fff;
        }

        &-close {
          display: none;
          position: absolute;
          top: 10px;
          right: 15px;
          width: 30px;
          height: 30px;
          cursor: pointer;

          .fa {
            display: none;
          }

          &::after,
          &::before {
            content: '';
            display: block;
            width: 31px;
            height: 2px;
            background: $darkgray;
            transition: background-color 125ms linear;
          }

          &::before {
            transform: rotate(-45deg) translateY(13px) translateX(-13px);
          }

          &::after {
            transform: rotate(45deg) translateY(12px) translateX(12px);
          }

          &:hover {
            &::before,
            &::after {
              background: $blue;
            }
          }
        }

        &-iframe {
          display: block;
          width: 100%;
          height: 100%;
          border: none;
        }

        &-footer {
          position: absolute;
          left: 0;
          bottom: 0;
          width: 100%;
          padding: 0 3rem;
          text-align: center;
          background-color: #fff;
          line-height: 5rem;
        }
      }
    }
  }
}

@keyframes modalEnter {
  0% {
    transform: scale(0.9) translateZ(0);
    opacity: 0;
  }
  100% {
    transform: scale(1) translateZ(0);
    opacity: 1;
  }
}

@keyframes modalLeave {
  0% {
    transform: scale(1) translateZ(0);
    opacity: 1;
  }
  100% {
    transform: scale(0.9) translateZ(0);
    opacity: 0;
  }
}

body.site--editmode-on .lf-modal[headline='Game settings'] {
  padding-top: 15rem;

  &::before {
    background: #2e2e2e;
  }
}
</style>
